// ============================================================
// fondos/catalogo.js — LOS FONDOS ANIMADOS: la fuente única (módulo PURO)
//
// Qué fondos hay, cómo se llaman, qué parámetros entiende cada uno (con su rango y su valor por
// defecto), si caben sobre una foto y si reaccionan al puntero. Antes esa información vivía
// repartida en seis listas sin dueño —`BG_CATALOG`, `BG_PARAMS`, los grupos del panel,
// `FONDOS_SOBRE_FOTO`, los rangos a mano de los deslizadores y `CANVAS_ALL/CSS_ALL` en Python—,
// y cada una podía decir algo distinto (P3 del plan del motor de fondos,
// `CLAUDE_RESUMEN_PLANES/2026-09-24_plan-p3-motor-efectos.md`).
//
// CONSUMIDORES (todos DERIVAN de aquí; ninguno lleva su copia):
//   · `animations/backgrounds.js` — `BG_CATALOG` y `BG_PARAMS` (sus nombres de siempre);
//   · `fondos/capacidad.js` — qué fondos caben sobre una foto (`sobreFoto`);
//   · `fondos/capa.js` — qué fondos son texturas (pintan en su propia capa CSS);
//   · `fondos/efectos/index.js` — lo que recibe cada efecto (sus parámetros, menos los de la capa);
//   · `fondos/config.js` — lo que se guarda (claves del fondo y sus rangos);
//   · `editor/bg-studio.js` — grupos, miniaturas y rangos de los deslizadores;
//   · el backend, por el build (`core_services/bg_meta.json`, clave `catalogo` →
//     `core_services/bg_meta.py`): `CANVAS_ALL`, `CSS_ALL`, `OVERLAYS` y `BG_VALUES` de
//     `llm_schemas.py`, y los rangos de `llm_sanitizers._sanear_bg_cfg`.
//
// CAMPOS DE UN FONDO:
//   id         el que se guarda en `props.bg_animation` (NO se renombra nunca: está en los sitios)
//   label      el nombre que ve el cliente
//   categoria  'animado' (se mueve) | 'textura' (CSS estático; también sirve de overlay)
//   tecnica    cómo se pinta: 'lienzo' (canvas, `fondos/lienzo.js`) | 'capas' (manchas CSS animadas en
//              el compositor, `fondos/capas.js`) | 'textura' (CSS estático, `fondos/texturas.js`)
//   grupo      subgrupo del panel (ver GRUPOS); las texturas van aparte
//   params     { clave: {tipo:'color'} | {min, max, paso, defecto} }; el ORDEN es el del panel
//   sobreFoto  cabe sobre una foto de fondo (decisión Q2 del dueño: solo los suaves, medidos)
//   interaccion  reacciona al puntero
//   alfaMax    (animados) el TOPE de alfa por polaridad de la sección, a opacidad 100. El motor se lo
//              pasa al efecto (`entorno.alfa`) y el efecto expresa sus alfas como fracciones de él:
//              · capas: el alfa del núcleo de sus manchas;
//              · lienzos: el de lo que pinta UNA llamada (un trazo, un punto, un carácter, un sprite).
//              La legibilidad va DENTRO del efecto (243 de 246 bloques de test222 guardan la opacidad
//              que puso la IA, así que un valor por defecto no bastaría), y el contrato de los tests lo
//              comprueba llamada a llamada. Calibrado con el arnés B de la F3 (§2.5 del plan).
//              Aurora y bokeh (29-09-2026) y humo, mesh y blobs, rehechos como lienzos (30-09),
//              recalibrados al reanimarlos (el dueño vio la intensidad «sutil» muerta, «se ven casi
//              todos igual»; en un lienzo el tope es por llamada y la ESTELA del humo acumula, de ahí
//              su tope bajo en claro): su tope es el MAYOR con el que, a
//              opacidad 100 y en el peor de 49 instantes (cada 0,5 s durante 24 s, reloj virtual), el
//              titular queda ≥ 3, el párrafo ≥ 4,5 y el anillo del botón ≥ 2,5 en claro, pastel y
//              oscuro, con un margen de un 3-12 % para lo que cae entre muestra y muestra. Por eso su
//              opacidad por defecto es 100: la vida sale del movimiento y de la forma, no del alfa
//   curvaOpacidad  (opcional) cómo se PINTA la opacidad guardada: 100·(op/100)^curva
//              (`opacidadPintada`). La llevan los 5 fondos recalibrados el 29/30-09-2026 (decisión del
//              dueño): casi todos sus bloques guardan la opacidad que eligió la IA (30-55) para los
//              efectos de antes, mucho más fuertes, y con la legibilidad ya dentro del efecto los dejaba
//              casi invisibles. Con 0,5: 45 → 67, 30 → 55, 100 → 100. El deslizador guarda y enseña el
//              valor de siempre; lo publicado, el editor y la vista previa pintan el mismo. Desde el
//              01-10-2026 también las 5 texturas, con su alfa recalibrado (`fondos/texturas.js::ALFA`): la
//              IA las deja al 20-60 % y así se quedaban sin verse
//   icono      SVG del panel (nunca emojis)
//
// MÓDULO PURO: ES2019 sin `?.` ni `??`, cero imports y cero DOM — lo importan el build (node 12)
// y los tests de node.
// ============================================================

// ── Rangos comunes (hoy son los mismos para todos los fondos; cada fondo puede tener los suyos) ──
const COLOR = { tipo: 'color' };
function opacidad(defecto) { return { min: 0, max: 100, paso: 1, defecto: defecto }; }
const VELOCIDAD = { min: 1, max: 10, paso: 1, defecto: 5 };
/** Estrellas: su velocidad sin dato es 0,6× (= 3 en la escala 1-10). Con el defecto común (5), abrir el
 *  panel y Aplicar las aceleraría un 67 % sin que nadie lo pidiera (E14: la velocidad no le llegaba). */
const VELOCIDAD_ESTRELLAS = { min: 1, max: 10, paso: 1, defecto: 3 };
const DENSIDAD = { min: 1, max: 10, paso: 1, defecto: 5 };
const SUAVIDAD = { min: 0, max: 100, paso: 1, defecto: 0 };
/** La curva de la opacidad de los fondos recalibrados y de las texturas (ver `curvaOpacidad`). */
const CURVA_PERCIBIDA = 0.5;

/** Subgrupos del panel, en el orden en que se enseñan. */
export const GRUPOS = [
    { id: 'particulas', label: 'Partículas y redes' },
    { id: 'ondas', label: 'Ondas y gradientes' },
    { id: 'geometricos', label: 'Geométricos y especiales' },
];

function svg(cuerpo) {
    return '<svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none">' + cuerpo + '</svg>';
}

/** Los 18 fondos, en el orden del panel. */
export const FONDOS = [
    { id: 'neural', label: 'Neural', categoria: 'animado', tecnica: 'lienzo', grupo: 'particulas',
      params: { color1: COLOR, opacity: opacidad(100), speed: VELOCIDAD, density: DENSIDAD },
      sobreFoto: true, interaccion: true, alfaMax: { claro: 0.9, oscuro: 1 },
      icono: svg('<circle cx="6" cy="6" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="12" cy="18" r="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="7" y1="8" x2="11" y2="16"/><line x1="17" y1="8" x2="13" y2="16"/>') },
    { id: 'particles', label: 'Partículas', categoria: 'animado', tecnica: 'lienzo', grupo: 'particulas',
      params: { color1: COLOR, opacity: opacidad(100), speed: VELOCIDAD, density: DENSIDAD },
      sobreFoto: true, interaccion: true, alfaMax: { claro: 0.8, oscuro: 0.9 },
      icono: svg('<circle cx="12" cy="12" r="1.5"/><circle cx="6" cy="7" r="1.5"/><circle cx="18" cy="8" r="1.5"/><circle cx="8" cy="17" r="1.5"/><circle cx="16" cy="16" r="1.5"/>') },
    { id: 'waves', label: 'Olas', categoria: 'animado', tecnica: 'lienzo', grupo: 'ondas',
      params: { color1: COLOR, opacity: opacidad(100), speed: VELOCIDAD },
      sobreFoto: false, interaccion: false, alfaMax: { claro: 0.45, oscuro: 0.55 },
      icono: svg('<path d="M2 6c3-1.5 5-1.5 8 0s5 1.5 8 0c3-1.5 4-1.5 6 0"/><path d="M2 12c3-1.5 5-1.5 8 0s5 1.5 8 0c3-1.5 4-1.5 6 0"/><path d="M2 18c3-1.5 5-1.5 8 0s5 1.5 8 0c3-1.5 4-1.5 6 0"/>') },
    { id: 'aurora', label: 'Aurora', categoria: 'animado', tecnica: 'capas', grupo: 'ondas',
      params: { color1: COLOR, color2: COLOR, opacity: opacidad(100), speed: VELOCIDAD, blur: SUAVIDAD },
      sobreFoto: false, interaccion: false, alfaMax: { claro: 0.33, oscuro: 0.35 }, curvaOpacidad: CURVA_PERCIBIDA,
      icono: svg('<path d="M4 18c0-3.5 2.5-6 6-6s6 2.5 6 6M8 18c0-2.5 1.5-4 4-4s4 1.5 4 4"/><circle cx="18" cy="8" r="3"/>') },
    { id: 'constellation', label: 'Estrellas', categoria: 'animado', tecnica: 'lienzo', grupo: 'particulas',
      params: { color1: COLOR, color2: COLOR, opacity: opacidad(100), speed: VELOCIDAD_ESTRELLAS, density: DENSIDAD },
      sobreFoto: true, interaccion: false, alfaMax: { claro: 0.8, oscuro: 0.9 },
      icono: svg('<path d="M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4-3.9-3.8 5.4-.8z"/>') },
    { id: 'smoke', label: 'Humo', categoria: 'animado', tecnica: 'lienzo', grupo: 'ondas',
      params: { color1: COLOR, opacity: opacidad(100), speed: VELOCIDAD, density: DENSIDAD, blur: SUAVIDAD },
      sobreFoto: false, interaccion: false, alfaMax: { claro: 0.068, oscuro: 0.33 }, curvaOpacidad: CURVA_PERCIBIDA,
      icono: svg('<path d="M4 18a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4H4z"/><path d="M7 14a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3H7z"/>') },
    { id: 'geometric', label: 'Geométrico', categoria: 'animado', tecnica: 'lienzo', grupo: 'geometricos',
      params: { color1: COLOR, opacity: opacidad(100), speed: VELOCIDAD },
      sobreFoto: true, interaccion: false, alfaMax: { claro: 0.4, oscuro: 0.85 },
      icono: svg('<polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"/>') },
    { id: 'bokeh', label: 'Bokeh', categoria: 'animado', tecnica: 'lienzo', grupo: 'particulas',
      params: { color1: COLOR, opacity: opacidad(100), density: DENSIDAD, blur: SUAVIDAD },
      sobreFoto: true, interaccion: false, alfaMax: { claro: 0.28, oscuro: 0.68 }, curvaOpacidad: CURVA_PERCIBIDA,
      icono: svg('<circle cx="8" cy="8" r="4"/><circle cx="16" cy="16" r="4"/><circle cx="16" cy="8" r="2"/>') },
    { id: 'matrix-rain', label: 'Matrix', categoria: 'animado', tecnica: 'lienzo', grupo: 'geometricos',
      params: { color1: COLOR, opacity: opacidad(100), speed: VELOCIDAD, density: DENSIDAD },
      sobreFoto: false, interaccion: false, alfaMax: { claro: 0.7, oscuro: 0.8 },
      icono: svg('<path d="M4 4v16M9 4v10M14 4v14M19 4v8"/>') },
    { id: 'mesh-gradient', label: 'Mesh Flow', categoria: 'animado', tecnica: 'lienzo', grupo: 'ondas',
      params: { color1: COLOR, color2: COLOR, opacity: opacidad(100), speed: VELOCIDAD, blur: SUAVIDAD },
      sobreFoto: false, interaccion: false, alfaMax: { claro: 0.23, oscuro: 0.23 }, curvaOpacidad: CURVA_PERCIBIDA,
      icono: svg('<circle cx="9" cy="10" r="6"/><circle cx="15" cy="14" r="6"/>') },
    { id: 'dot-grid', label: 'Dot Grid', categoria: 'animado', tecnica: 'lienzo', grupo: 'geometricos',
      params: { color1: COLOR, opacity: opacidad(100), density: DENSIDAD, speed: VELOCIDAD },
      sobreFoto: true, interaccion: true, alfaMax: { claro: 1, oscuro: 1 },
      icono: svg('<circle cx="6" cy="6" r="1"/><circle cx="12" cy="6" r="1"/><circle cx="18" cy="6" r="1"/><circle cx="6" cy="12" r="1.6"/><circle cx="12" cy="12" r="2.2"/><circle cx="18" cy="12" r="1.6"/><circle cx="6" cy="18" r="1"/><circle cx="12" cy="18" r="1"/><circle cx="18" cy="18" r="1"/>') },
    { id: 'topographic', label: 'Topográfico', categoria: 'animado', tecnica: 'lienzo', grupo: 'geometricos',
      params: { color1: COLOR, opacity: opacidad(100), speed: VELOCIDAD, density: DENSIDAD },
      sobreFoto: true, interaccion: false, alfaMax: { claro: 0.2, oscuro: 0.28 },
      icono: svg('<path d="M3 9 Q8 5 12 9 T21 9"/><path d="M3 13 Q8 9 12 13 T21 13"/><path d="M3 17 Q8 13 12 17 T21 17"/>') },
    { id: 'blobs-morph', label: 'Blobs', categoria: 'animado', tecnica: 'lienzo', grupo: 'ondas',
      params: { color1: COLOR, color2: COLOR, opacity: opacidad(100), speed: VELOCIDAD, density: DENSIDAD, blur: SUAVIDAD },
      sobreFoto: false, interaccion: false, alfaMax: { claro: 0.21, oscuro: 0.37 }, curvaOpacidad: CURVA_PERCIBIDA,
      icono: svg('<path d="M12 3c4 0 7 3 6 7s2 5 1 8-5 3-8 2-7-3-6-8 3-9 7-9z"/>') },
    // ── Texturas CSS estáticas: se pintan en su propia capa y sirven también de OVERLAY ──
    { id: 'gradient-mesh', label: 'Mesh', categoria: 'textura', tecnica: 'textura', grupo: 'texturas',
      params: { color1: COLOR, opacity: opacidad(100) }, sobreFoto: true, interaccion: false, curvaOpacidad: CURVA_PERCIBIDA,
      icono: svg('<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 3v18M3 12h18"/>') },
    { id: 'grid', label: 'Grid', categoria: 'textura', tecnica: 'textura', grupo: 'texturas',
      params: { color1: COLOR, opacity: opacidad(100) }, sobreFoto: true, interaccion: false, curvaOpacidad: CURVA_PERCIBIDA,
      icono: svg('<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M15 3v18M3 9h18M3 15h18"/>') },
    { id: 'radial-glow', label: 'Glow', categoria: 'textura', tecnica: 'textura', grupo: 'texturas',
      params: { color1: COLOR, opacity: opacidad(100) }, sobreFoto: true, interaccion: false, curvaOpacidad: CURVA_PERCIBIDA,
      icono: svg('<circle cx="12" cy="12" r="8"/>') },
    { id: 'diagonal-lines', label: 'Diagonal', categoria: 'textura', tecnica: 'textura', grupo: 'texturas',
      params: { color1: COLOR, opacity: opacidad(100) }, sobreFoto: true, interaccion: false, curvaOpacidad: CURVA_PERCIBIDA,
      icono: svg('<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="21" x2="21" y2="3"/>') },
    { id: 'dot-matrix', label: 'Puntos', categoria: 'textura', tecnica: 'textura', grupo: 'texturas',
      params: { color1: COLOR, opacity: opacidad(100) }, sobreFoto: true, interaccion: false, curvaOpacidad: CURVA_PERCIBIDA,
      icono: svg('<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8" cy="8" r="1"/><circle cx="16" cy="8" r="1"/><circle cx="8" cy="16" r="1"/><circle cx="16" cy="16" r="1"/>') },
];

const _POR_ID = {};
FONDOS.forEach(function (f) { _POR_ID[f.id] = f; });

/** Los ids, en el orden del panel. */
export const IDS = FONDOS.map(function (f) { return f.id; });

/**
 * Fondos RETIRADOS que siguen guardados en sitios (el `props.bg_animation` de los datos y el
 * `data-bg-anim` horneado en lo publicado): se pintan como su SUSTITUTO, con su misma config. El id no
 * se reutiliza nunca. «Fluido» (`gradient-flow`) se retiró el 30-09-2026 (el dueño: «no me gusta su
 * existencia»); sus secciones pasan a Mesh Flow, lo más parecido (dos colores, suave, cubre la
 * sección). El build lo exporta al backend (`bg_meta.RETIRADOS`), que lo aplica al sanear.
 */
export const RETIRADOS = { 'gradient-flow': 'mesh-gradient' };

/** El id con el que se PINTA un fondo guardado: el mismo, o su sustituto si se retiró. */
export function idVigente(id) {
    return (typeof id === 'string' && Object.prototype.hasOwnProperty.call(RETIRADOS, id)) ? RETIRADOS[id] : id;
}

/**
 * El descriptor de un fondo, o null. `hasOwnProperty` a propósito: un id guardado como
 * 'constructor' o 'toString' no puede pasar por un fondo (mismo criterio que
 * `fondos/motor.js::esFondoConocido`).
 */
export function porId(id) {
    return (typeof id === 'string' && Object.prototype.hasOwnProperty.call(_POR_ID, id)) ? _POR_ID[id] : null;
}

export function esFondoDelCatalogo(id) {
    return porId(id) !== null;
}

/** Los que se mueven (hoy lienzo; en el P3, lienzo o capas) y las texturas estáticas. */
export const ANIMADOS = FONDOS.filter(function (f) { return f.categoria === 'animado'; }).map(function (f) { return f.id; });
export const TEXTURAS = FONDOS.filter(function (f) { return f.categoria === 'textura'; }).map(function (f) { return f.id; });

/** Los overlays combinables sobre un fondo animado: las texturas. */
export const OVERLAYS = TEXTURAS.slice();

export function esOverlay(id) {
    return typeof id === 'string' && OVERLAYS.indexOf(id) !== -1;
}

/** Las claves de parámetro que entiende un fondo, en el orden del panel ([] si no existe). */
export function parametrosDe(id) {
    const f = porId(id);
    return f ? Object.keys(f.params) : [];
}

/** El rango `{min, max, paso, defecto}` de un parámetro numérico de un fondo, o null. */
export function rangoDe(id, clave) {
    const f = porId(id);
    const r = f && Object.prototype.hasOwnProperty.call(f.params, clave) ? f.params[clave] : null;
    return (r && r.tipo !== 'color') ? r : null;
}

/**
 * El TOPE de alfa de un fondo animado sobre una sección de polaridad `oscuro` (ver `alfaMax` en la
 * cabecera), o null si el fondo no lo declara (las texturas).
 */
export function alfaMaxDe(id, oscuro) {
    const f = porId(id);
    if (!f || !f.alfaMax) return null;
    return oscuro ? f.alfaMax.oscuro : f.alfaMax.claro;
}

/**
 * La opacidad (0-100) con la que se PINTA un fondo cuya opacidad guardada es `op`: la misma, salvo en
 * los fondos con `curvaOpacidad`, que la pasan por su curva. Sin valor (o un valor que no es un número)
 * la devuelve tal cual: de eso se ocupa quien la aplica.
 */
export function opacidadPintada(id, op) {
    const f = porId(id);
    const n = (op === undefined || op === null || op === '') ? NaN : Number(op);
    if (!f || !f.curvaOpacidad || !isFinite(n)) return op;
    const x = Math.max(0, Math.min(100, n)) / 100;
    return Math.round(100 * Math.pow(x, f.curvaOpacidad));
}

/**
 * La opacidad GUARDADA más alta que se pinta sin pasar de `tope` (0-100). Es el máximo del deslizador
 * del panel, que así no ofrece un tramo que no cambia nada. Sin curva, el propio tope. Con curva, menos:
 * sobre una foto (tope 80), bokeh guarda hasta 64, que ya se pinta a 80. De 65 a 80 también se pintaría
 * a 80, porque el tope corta DESPUÉS de la curva (revisor del cierre de la F3, 01-10-2026).
 */
export function opacidadGuardadaMaxima(id, tope) {
    const t = Number(tope);
    if (!isFinite(t)) return 100;
    let x = Math.max(0, Math.min(100, Math.floor(t)));
    while (x > 0 && opacidadPintada(id, x) > t) x--;
    return x;
}

/** Los ids que caben sobre una foto de fondo, en el orden del panel. */
export const FONDOS_SOBRE_FOTO_CATALOGO = FONDOS.filter(function (f) { return f.sobreFoto; }).map(function (f) { return f.id; });

// ── Las formas de siempre, para quien las importa con su nombre histórico ────────────────────

/** `[{id, label, category: 'canvas'|'css', icon}]`: la forma de `backgrounds.js::BG_CATALOG`. */
export function catalogoCompat() {
    return FONDOS.map(function (f) {
        return { id: f.id, label: f.label, category: f.categoria === 'textura' ? 'css' : 'canvas', icon: f.icono };
    });
}

/** `{id: [claves]}`: la forma de `backgrounds.js::BG_PARAMS`. */
export function parametrosCompat() {
    const out = {};
    FONDOS.forEach(function (f) { out[f.id] = Object.keys(f.params); });
    return out;
}

/** Lo que el build exporta al backend (`core_services/bg_meta.json`, clave `catalogo`). */
export function catalogoParaElBackend() {
    return {
        fondos: FONDOS.map(function (f) {
            const params = {};
            Object.keys(f.params).forEach(function (k) { params[k] = Object.assign({}, f.params[k]); });
            return { id: f.id, categoria: f.categoria, tecnica: f.tecnica, params: params,
                     sobreFoto: f.sobreFoto, interaccion: f.interaccion };
        }),
        overlays: OVERLAYS.slice(),
        retirados: Object.assign({}, RETIRADOS),
    };
}
